/* eslint-disable max-lines */
import React, {
 useState, useEffect, useMemo
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
    Card, CardContent, CardHeader, CardTitle,
} from 'shared/components/ui/card';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 Label
} from 'shared/components/ui/label';
import {
 Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Loader2, Copy, Check
} from 'lucide-react';
import * as a2aClient from 'shared/api/services/BEX/a2aClient';
import {
    Assistant,
} from 'builders/builderAssistant/types/builderAssistant.types';

interface AssistantTestProps {
    assistant: Assistant;
}

// A-0007.06: routing decision block surfaced by Mind on non-streaming responses.
// Mind injects this into the executor result dict, which flows through A2A
// DataPart → a2aClient.extractOutputFromTask → result.output._bex_routing.
// Absent when Mind hasn't been updated yet or on streaming responses — banner
// renders only when present.
interface BexRouting {
    selected_opcode_id: string | null;
    resolution_mode: string | null;
    reason: string | null;
}

// Color tokens per resolution_mode. Unknown modes fall back to the neutral
// style so future modes don't break rendering.
const ROUTING_STYLES: Record<string, { chip: string; icon: string }> = {
    llm_match: {
        chip: 'bg-blue-100 text-blue-900 border-blue-300',
        icon: '🤖',
    },
    single_opcode: {
        chip: 'bg-green-100 text-green-900 border-green-300',
        icon: '⚡',
    },
    default_fallback: {
        chip: 'bg-amber-100 text-amber-900 border-amber-300',
        icon: '↩',
    },
    override: {
        chip: 'bg-purple-100 text-purple-900 border-purple-300',
        icon: '🎯',
    },
    default_only: {
        chip: 'bg-slate-100 text-slate-800 border-slate-300',
        icon: '•',
    },
    no_intent: {
        chip: 'bg-slate-100 text-slate-800 border-slate-300',
        icon: '•',
    },
    no_candidates: {
        chip: 'bg-slate-100 text-slate-800 border-slate-300',
        icon: '•',
    },
};

// A-0007.05 (2026-04-22): test panel migrated from the deleted /v3/assistant/execute
// endpoint to A2A v1.0 JSON-RPC SendMessage / SendStreamingMessage methods.
// Input form is unchanged — the typed_input → A2A Parts conversion happens
// inside the a2aClient helper. Categories + fields remain pre-populated from
// the assistant doc so the UI stays honest with the runtime contract.
export default function AssistantTest({
 assistant
}: AssistantTestProps) {
    const {
 oragID, subOragID
} = useParams<{ oragID: string; subOragID: string }>();

    const ti = assistant.typed_input;
    const authorizedChannels = assistant.channels?.authorized_channels || [];
    const channelMode = assistant.channels?.mode || 'restricted';

    // ── Form state ────────────────────────────────────────────────────────
    // Compute "needed keys" per category. Sources:
    //   1. What the assistant declared in typed_input
    //   2. What its opcodes[].input_mapping actually references
    // Assistants created/edited before field declarations caught up may still
    // have mappings pointing to undeclared fields. Showing the union (with an
    // "implicit" marker on mapping-only keys) keeps the test UI complete.
    const mappingRefs = useMemo(() => {
        const refs = {
 context: new Set<string>(),
propensity: new Set<string>(),
entity: new Set<string>()
};
        for (const entry of assistant.opcodes || []) {
            for (const target of Object.values(entry.input_mapping || {})) {
                const m = (target || '').match(/^(context|propensity|entity)\.(.+)$/);
                if (m) refs[m[1] as 'context' | 'propensity' | 'entity'].add(m[2]);
            }
        }
        return refs;
    }, [assistant.opcodes]);

    const declaredContextKeys = useMemo(() => ti?.context_keys || [], [ti]);
    const declaredPropensityKeys = useMemo(() => ti?.propensity_keys || [], [ti]);
    const declaredEntityKeys = useMemo(
        () => Object.keys(ti?.entity_schema?.properties || {}),
        [ti]
    );

    // NOTE: avoid `[...someSet]` — the project's TS target rejects iterator
    // spread without --downlevelIteration. `Array.from(set)` works on every
    // target and lets us merge with the declared array via concat.
    const neededContextKeys = useMemo(
        () => Array.from(new Set(declaredContextKeys.concat(Array.from(mappingRefs.context)))),
        [declaredContextKeys, mappingRefs.context]
    );
    const neededPropensityKeys = useMemo(
        () => Array.from(new Set(declaredPropensityKeys.concat(Array.from(mappingRefs.propensity)))),
        [declaredPropensityKeys, mappingRefs.propensity]
    );
    const neededEntityKeys = useMemo(
        () => Array.from(new Set(declaredEntityKeys.concat(Array.from(mappingRefs.entity)))),
        [declaredEntityKeys, mappingRefs.entity]
    );

    const isImplicit = (cat: 'context' | 'propensity' | 'entity', k: string) => mappingRefs[cat].has(k) &&
        (cat === 'context' ? !declaredContextKeys.includes(k)
            : cat === 'propensity' ? !declaredPropensityKeys.includes(k)
                : !declaredEntityKeys.includes(k));

    const [contextVals, setContextVals] = useState<Record<string, string>>({});
    const [propensityVals, setPropensityVals] = useState<Record<string, string>>({});
    const [entityVals, setEntityVals] = useState<Record<string, string>>({});
    const [intent, setIntent] = useState<string>('');
    const [opcodeIdOverride, setOpcodeIdOverride] = useState<string>('');
    const [channelId, setChannelId] = useState<string>(
        channelMode === 'restricted' ? (authorizedChannels[0] || '') : ''
    );
    const [memoryLogId, setMemoryLogId] = useState<string>(`test-${Date.now()}`);
    const [stream, setStream] = useState(false);

    const [lastPayload, setLastPayload] = useState<any>(null);
    const [output, setOutput] = useState<string>('');
    // A-0007.06: parsed `_bex_routing` from the last response (null when absent)
    const [routing, setRouting] = useState<BexRouting | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    // Seed needed fields (declared ∪ referenced-by-mapping) with empty strings.
    useEffect(() => {
        setContextVals((prev) => {
            const next: Record<string, string> = {};
            for (const k of neededContextKeys) next[k] = prev[k] || '';
            return next;
        });
        setPropensityVals((prev) => {
            const next: Record<string, string> = {};
            for (const k of neededPropensityKeys) next[k] = prev[k] || '';
            return next;
        });
        setEntityVals((prev) => {
            const next: Record<string, string> = {};
            for (const k of neededEntityKeys) next[k] = prev[k] || '';
            return next;
        });
    }, [neededContextKeys, neededPropensityKeys, neededEntityKeys]);

    // Coerce a user-typed string into a JSON value when reasonable. Lets the
    // builder type raw text for strings but still get numbers/bools/objects
    // by typing JSON literals (e.g. 42, true, {"foo":"bar"}).
    const coerce = (raw: string): any => {
        if (raw === '') return '';
        try { return JSON.parse(raw); } catch { return raw; }
    };

    const buildTypedInput = () => ({
        context: Object.fromEntries(
            Object.entries(contextVals)
                .filter(([, v]) => v !== '')
                .map(([k, v]) => [k, coerce(v)])
        ),
        propensity: Object.fromEntries(
            Object.entries(propensityVals)
                .filter(([, v]) => v !== '')
                .map(([k, v]) => [k, coerce(v)])
        ),
        intent: intent ? coerce(intent) : null,
        entity: Object.fromEntries(
            Object.entries(entityVals)
                .filter(([, v]) => v !== '')
                .map(([k, v]) => [k, coerce(v)])
        ),
    });

    // A-0007.05: display-only — show the exact A2A JSON-RPC envelope the
    // helper will send. Makes copy-from-UI authoritative for curl testing.
    const buildDisplayPayload = () => {
        const typedInput = buildTypedInput();
        const message: any = {
            role: 'user',
            parts: a2aClient.buildPartsFromTypedInput(typedInput),
        };
        if (memoryLogId) message.contextId = memoryLogId;
        const bexExtensions: Record<string, any> = {};
        if (opcodeIdOverride) bexExtensions.opcodeId = opcodeIdOverride;
        if (channelId) bexExtensions.channelId = channelId;
        const params: any = {
 message
};
        if (Object.keys(bexExtensions).length > 0) {
            params.configuration = {
 bexExtensions
};
        }
        return {
            _endpoint: `/assistant/a2a/${oragID}/${subOragID}/${assistant.assistant_id}/`
                + (stream ? 'stream' : 'message'),
            _a2a_method: stream ? 'SendStreamingMessage' : 'SendMessage',
            _bearer_token: '(injected by bex-server — never sent from the browser)',
            envelope: {
                jsonrpc: '2.0',
                id: 1,
                method: stream ? 'SendStreamingMessage' : 'SendMessage',
                params,
            },
        };
    };

    const handleRun = async () => {
        setIsLoading(true);
        setOutput('');
        setLastPayload(null);
        setRouting(null);
        try {
            const typedInput = buildTypedInput();
            const display = buildDisplayPayload();
            setLastPayload(display);

            // Reads redact the token, so `has_token` is the only signal the
            // browser has about whether the stored DE can be run at all.
            const deHasToken = assistant.has_token ?? !!assistant.assistant_token;
            if (!deHasToken) {
                setOutput('Error: this assistant has no assistant_token configured. '
                    + 'A2A requires a Bearer token — set one on the assistant doc first.');
                return;
            }

            // No token in cfg: bex-server injects the DE's credential per call.
            const cfg: a2aClient.A2AConfig = {
                orgId: oragID || '',
                subOrgId: subOragID || '',
                assistantId: assistant.assistant_id,
            };
            const opts: a2aClient.SendMessageOpts = {
                typedInput,
                memoryLogId: memoryLogId || undefined,
                opcodeId: opcodeIdOverride || undefined,
                channelId: channelId || undefined,
                // Default streaming extensions; the test panel doesn't expose
                // these knobs yet — fine for now (accepted as defaults on
                // the Mind side).
            };

            if (stream) {
                // SSE mode — collect events and render them as a list.
                const events: any[] = [];
                try {
                    for await (const evt of a2aClient.sendStreamingMessage(cfg, opts)) {
                        events.push(evt);
                        setOutput(JSON.stringify(events, null, 2));
                    }
                } catch (streamErr: any) {
                    events.push({
 _error: streamErr?.message || String(streamErr)
});
                    setOutput(JSON.stringify(events, null, 2));
                }
            } else {
                const result = await a2aClient.sendMessage(cfg, opts);
                // A-0007.06: Mind injects `_bex_routing` into the executor
                // result (non-streaming). Extract for the banner above; leaves
                // the field visible in the JSON below so the raw contract
                // stays authoritative.
                const br = result.output && typeof result.output === 'object'
                    ? result.output._bex_routing
                    : null;
                if (br && typeof br === 'object') {
                    setRouting({
                        selected_opcode_id: br.selected_opcode_id ?? null,
                        resolution_mode: br.resolution_mode ?? null,
                        reason: br.reason ?? null,
                    });
                }
                // Show the full envelope: state, output, errors, full task.
                setOutput(JSON.stringify({
                    state: result.state,
                    taskId: result.taskId,
                    output: result.output,
                    errors: result.errors,
                    task: result.task,
                }, null, 2));
            }
        } catch (err: any) {
            const msg = err?.response?.data || err?.message || 'Unknown error';
            setOutput(typeof msg === 'string' ? msg : JSON.stringify(msg, null, 2));
        } finally {
            setIsLoading(false);
        }
    };

    const copyPayload = async () => {
        const text = JSON.stringify(buildDisplayPayload(), null, 2);
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
    };

    const allOpcodes: Array<{ id: string; label: string }> = (assistant.opcodes || []).map((e) => ({
        id: e.opcode_id,
        label: `${e.opcode_id}${e.purpose ? ` — ${e.purpose}` : ''}`,
    }));

    return (
        <Card className="flex-grow h-full">
            <CardHeader>
                <CardTitle>Test Assistant (A2A v1.0)</CardTitle>
                <div className="text-xs text-muted-foreground">
                    Calls A2A v1.0&nbsp;
                    <code>{stream ? 'SendStreamingMessage' : 'SendMessage'}</code>
                    &nbsp;on&nbsp;
                    <code>/v1/a2a/{'{org}'}/{'{sub}'}/{'{assistant_id}'}/rpc</code>
                    &nbsp;with the typed_input mapped into A2A Message Parts
                    (metadata.category + metadata.name per the BeX typed-input
                    extension). Fields below are seeded from what you declared
                    on the assistant doc.
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* ── Top: routing + memory controls ── */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label>OpCode override <small className="text-muted-foreground">(optional)</small></Label>
                        <Select value={opcodeIdOverride || '__none__'} onValueChange={(v) => setOpcodeIdOverride(v === '__none__' ? '' : v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">
                                    <span className="text-muted-foreground italic">
                                        route via intent / default ({assistant.default_opcode || 'none'})
                                    </span>
                                </SelectItem>
                                {allOpcodes.map((op) => (
                                    <SelectItem key={op.id} value={op.id}>
                                        <span className="font-mono text-xs">{op.label}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>
                            Channel
                            <small className="text-muted-foreground ml-1">
                                ({channelMode})
                            </small>
                        </Label>
                        {channelMode === 'restricted' && authorizedChannels.length > 0 ? (
                            <Select value={channelId} onValueChange={setChannelId}>
                                <SelectTrigger><SelectValue placeholder="pick authorized channel" /></SelectTrigger>
                                <SelectContent>
                                    {authorizedChannels.map((id) => (
                                        <SelectItem key={id} value={id}>
                                            <span className="font-mono text-xs">{id}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Input
                                value={channelId}
                                onChange={(e) => setChannelId(e.target.value)}
                                placeholder={channelMode === 'open' ? 'any channel_id (or blank)' : '— restricted with no channels, will 403 —'}
                                className="text-xs font-mono"
                            />
                        )}
                    </div>
                    <div>
                        <Label>Memory log id</Label>
                        <Input
                            value={memoryLogId}
                            onChange={(e) => setMemoryLogId(e.target.value)}
                            placeholder="conversation id"
                            className="text-xs font-mono"
                        />
                    </div>
                </div>

                {/* ── Typed Input editor ── */}
                <div className="border rounded-md p-3 space-y-3 bg-muted/10">
                    <div className="flex items-center justify-between">
                        <Label className="font-semibold">typed_input</Label>
                        <span className="text-xs text-muted-foreground">
                            values coerce as JSON when parseable; otherwise kept as strings
                        </span>
                    </div>
                    <TypedInputCategory
                        title="context"
                        hint="soft — unknown keys warned, not rejected"
                        keys={neededContextKeys}
                        implicit={(k) => isImplicit('context', k)}
                        values={contextVals}
                        onChange={setContextVals}
                    />
                    <TypedInputCategory
                        title="propensity"
                        hint="soft — unknown keys warned, not rejected"
                        keys={neededPropensityKeys}
                        implicit={(k) => isImplicit('propensity', k)}
                        values={propensityVals}
                        onChange={setPropensityVals}
                    />
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <Label className="text-xs font-semibold">intent</Label>
                            <span className="text-xs text-muted-foreground">
                                {ti?.intent?.format === 'structured'
                                    ? 'structured — type JSON'
                                    : 'text — type the user intent'}
                            </span>
                        </div>
                        <Textarea
                            rows={2}
                            value={intent}
                            onChange={(e) => setIntent(e.target.value)}
                            placeholder={ti?.intent?.format === 'structured' ? '{"action": "lookup"}' : 'check my order status'}
                            className="text-xs font-mono"
                        />
                        <p className="text-[11px] text-muted-foreground">
                            routing mode: <code>{ti?.intent?.routing_mode || 'default_only'}</code>
                        </p>
                    </div>
                    <TypedInputCategory
                        title="entity"
                        hint="strict — validated against entity_schema; rejects on mismatch"
                        keys={neededEntityKeys}
                        implicit={(k) => isImplicit('entity', k)}
                        values={entityVals}
                        onChange={setEntityVals}
                    />
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Switch checked={stream} onCheckedChange={(v) => setStream(!!v)} />
                        <Label className="text-xs">Stream (NDJSON)</Label>
                    </div>
                    <Button onClick={handleRun} disabled={isLoading}>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Run test
                    </Button>
                </div>

                {lastPayload && (
                    <div className="space-y-1">
                        <div className="flex items-center justify-between">
                            <Label>Last request payload</Label>
                            <Button variant="outline" size="sm" onClick={copyPayload}>
                                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                            </Button>
                        </div>
                        <pre className="rounded-md border bg-muted p-2 font-mono text-xs whitespace-pre-wrap overflow-auto max-h-[300px]">
                            {JSON.stringify(lastPayload, null, 2)}
                        </pre>
                    </div>
                )}

                {/* A-0007.06: routing decision banner. Only renders when Mind
                    populated `_bex_routing` on the response. Degrades gracefully
                    when absent (older Mind deploy, streaming, or non-dict result). */}
                {routing && (
                    <div className="space-y-1">
                        <Label>Routing decision</Label>
                        <div
                            className={
                                'rounded-md border px-3 py-2 text-xs space-y-1 '
                                + (ROUTING_STYLES[routing.resolution_mode || '']?.chip
                                    || 'bg-slate-100 text-slate-800 border-slate-300')
                            }
                        >
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-base">
                                    {ROUTING_STYLES[routing.resolution_mode || '']?.icon || '•'}
                                </span>
                                <span className="font-semibold">
                                    Opcode:
                                </span>
                                <code className="font-mono font-semibold">
                                    {routing.selected_opcode_id || '(none)'}
                                </code>
                                <span className="px-2 py-0.5 rounded border bg-white/60 text-[11px] uppercase tracking-wide font-semibold">
                                    {routing.resolution_mode || 'unknown'}
                                </span>
                            </div>
                            {routing.reason && (
                                <div className="text-[11px] opacity-90 leading-relaxed">
                                    {routing.reason}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="space-y-1">
                    <Label>Output</Label>
                    <pre className="rounded-md border bg-muted p-3 font-mono text-xs min-h-[140px] whitespace-pre-wrap overflow-auto max-h-[400px]">
                        {output || <span className="text-muted-foreground">No output yet.</span>}
                    </pre>
                </div>
            </CardContent>
        </Card>
    );
}

// Per-category editor. Shows ONLY the keys defined at build time —
// declared explicitly on typed_input or referenced by an
// opcodes[].input_mapping. No ad-hoc / runtime key invention: testers
// fill values for what the assistant promises to consume, nothing else.
// Mapping-only keys get an "implicit" pill so it's clear where they came
// from when they're not in the declared set.
function TypedInputCategory({
    title, hint, keys, implicit, values, onChange,
}: {
    title: string;
    hint: string;
    keys: string[];
    implicit?: (k: string) => boolean;
    values: Record<string, string>;
    onChange: (next: Record<string, string>) => void;
}) {
    const hasImplicit = keys.some((k) => implicit?.(k));

    return (
        <div className="space-y-1">
            <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold">{title}</Label>
                <span className="text-xs text-muted-foreground">{hint}</span>
            </div>
            {hasImplicit && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Some keys below are needed by an opcode&apos;s input_mapping but
                    aren&apos;t declared on the assistant&apos;s typed_input. Add them via
                    the builder for cleanliness; runtime will still accept them.
                </div>
            )}
            {keys.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">
                    Nothing declared for this category and no opcode mappings reference it — leave empty.
                </div>
            ) : (
                <div className="space-y-1">
                    {keys.map((k) => {
                        const isImpl = implicit?.(k);
                        return (
                            <div key={k} className="grid grid-cols-[1fr_2fr] gap-2 items-center">
                                <span className="font-mono text-xs text-muted-foreground flex items-center gap-1">
                                    {k}
                                    {isImpl && (
                                        <span className="text-[9px] uppercase tracking-wider px-1 py-0 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                            implicit
                                        </span>
                                    )}
                                </span>
                                <Input
                                    value={values[k] || ''}
                                    onChange={(e) => onChange({
 ...values,
[k]: e.target.value
})}
                                    placeholder="value (or JSON literal)"
                                    className="text-xs font-mono"
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
